/** Card network detected from PAN / IIN (first digits). */

export type PaymentCardBrand =
  | 'visa'
  | 'mastercard'
  | 'amex'
  | 'diners'
  | 'discover'
  | 'unknown';

function digitsOnly(value: string): string {
  return value.replace(/\D+/g, '');
}

/**
 * Detect brand from a full or partial card number (BIN/IIN).
 * Prefer longer prefixes first where ranges overlap.
 */
export function detectPaymentCardBrand(raw: string): PaymentCardBrand {
  const digits = digitsOnly(raw);
  if (!digits) {
    return 'unknown';
  }

  // American Express: 34, 37
  if (/^3[47]/.test(digits)) {
    return 'amex';
  }

  // Diners Club: 300–305, 36, 38
  if (/^3(?:0[0-5]|[68])/.test(digits)) {
    return 'diners';
  }

  // Discover: 6011, 65, 644–649
  if (/^(6011|65|64[4-9])/.test(digits)) {
    return 'discover';
  }

  // Mastercard: 51–55, 2221–2720
  if (/^5[1-5]/.test(digits)) {
    return 'mastercard';
  }
  if (/^2[2-7]/.test(digits) && digits.length >= 4) {
    const bin4 = Number(digits.slice(0, 4));
    if (bin4 >= 2221 && bin4 <= 2720) {
      return 'mastercard';
    }
  } else if (/^2/.test(digits) && digits.length < 4) {
    // Incomplete 2xxx — likely Mastercard 2-series while typing
    return 'mastercard';
  }

  // Visa: 4
  if (/^4/.test(digits)) {
    return 'visa';
  }

  return 'unknown';
}

export function paymentCardBrandLabel(brand: PaymentCardBrand): string {
  switch (brand) {
    case 'visa':
      return 'Visa';
    case 'mastercard':
      return 'Mastercard';
    case 'amex':
      return 'Amex';
    case 'diners':
      return 'Diners';
    case 'discover':
      return 'Discover';
    default:
      return 'Card';
  }
}

export function parseStoredPaymentCardBrand(raw: unknown): PaymentCardBrand {
  if (typeof raw !== 'string') {
    return 'unknown';
  }
  const value = raw.trim().toLowerCase();
  if (
    value === 'visa' ||
    value === 'mastercard' ||
    value === 'amex' ||
    value === 'diners' ||
    value === 'discover'
  ) {
    return value;
  }
  return 'unknown';
}
